import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Loader, Modal } from "semantic-ui-react";

import { useCan } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { EditChecklistActions, EditChecklistForm, QuestionGroups } from "modules/ChecklistsDashboard/components/EditChecklistModal/components";
import "modules/ChecklistsDashboard/components/EditChecklistModal/EditChecklistModal.scss";
import { useUpdateChecklistMutation } from "modules/ChecklistsDashboard/components/EditChecklistModal/hooks";
import { useChecklistDetailsQuery } from "modules/ChecklistsDashboard/hooks";

import type { Checklist, ChecklistID } from "models";
import type { CHECKLIST_LISTS } from "modules/ChecklistsDashboard/util";

type EditChecklistModalProps = {
  currentList: CHECKLIST_LISTS;
  checklistID?: ChecklistID;
  onClose: () => void;
  onOpenChecklist: (checklistID: ChecklistID) => void;
};

const FormHelper = createFormHelper<Checklist>();

export const EditChecklistModal = ({ checklistID, currentList, onClose, onOpenChecklist }: EditChecklistModalProps) => {
  const { t } = useTranslation();

  const { data: checklist, isFetching } = useChecklistDetailsQuery(checklistID);
  const { mutateAsync: updateChecklist, isPending } = useUpdateChecklistMutation();

  const canUpdate = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Checklists);
  const canUpdateDefault = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.DefaultChecklists);

  const canEdit = checklist?.dealer_id ? canUpdate : canUpdateDefault;
  const originalChecklist = checklist?.parent_checklist ?? checklist;

  const handleSubmit = async (values: Checklist) => {
    if (values.question_groups?.some(questionGroup => !questionGroup.name.trim())) return toast.error(t("v8_group_name_cannot_be_empty", "Group name cannot be empty"));

    await updateChecklist(values);
    onClose();
  };

  return (
    <Modal size="large" className="EditChecklistModal" open={!!checklistID} onClose={onClose} closeOnDimmerClick={false}>
      <Modal.Header>{t("v8_edit_checklist", "Edit Checklist")}</Modal.Header>

      {(isFetching || !checklist) && (
        <Modal.Content>
          <Loader active inline="centered" />
        </Modal.Content>
      )}

      {!isFetching && checklist && originalChecklist && (
        <FormHelper.Form
          onSubmit={handleSubmit}
          defaultValues={{
            ...checklist,
            template_id: originalChecklist.template_id,
            checklist_type: originalChecklist.checklist_type,
            active: originalChecklist.active,
            is_active_for_mechanic: originalChecklist.is_active_for_mechanic,
            include_in_reports: originalChecklist.include_in_reports,
            is_category_mode: originalChecklist.is_category_mode,
            check_in_customer: originalChecklist.check_in_customer,
            brand_group_ids: originalChecklist.brand_groups?.map(brandGroup => brandGroup.id) ?? [],
            tag_ids: originalChecklist.tags?.map(tag => tag.id) ?? [],
            question_groups: checklist.question_groups ?? []
          }}
        >
          <Modal.Content scrolling>
            <EditChecklistForm canEdit={canEdit} />
            <QuestionGroups canEdit={canEdit} />
          </Modal.Content>

          <EditChecklistActions
            checklist={checklist}
            currentList={currentList}
            canEdit={canEdit}
            isSubmitting={isPending}
            onClose={onClose}
            onOpenChecklist={onOpenChecklist}
          />
        </FormHelper.Form>
      )}
    </Modal>
  );
};
